// frontend/src/pages/Notifications.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import MD3Switch from '../components/MD3Switch';
import TutorialModal from '../components/TutorialModal';
import { hasSeenTutorial } from '../utils/tutorialData';
import {
    NotificationIcon as Bell,
    MailIcon as Mail,
    SmartphoneIcon as Smartphone,
    SaveIcon as Save,
    CheckCircleIcon as CheckCircle2,
    ShieldAlertIcon as ShieldAlert,
    ChevronDownIcon as ChevronDown,
    ChevronRightIcon as ChevronRight,
    DescriptionIcon as FileText,
    LoaderIcon as Loader2
} from '../components/Icons';

const Notifications = () => {
    const { API_URL, user } = useContext(AuthContext);
    const [settings, setSettings] = useState({
        events: { push: true, email: true },
        blog: { push: true, email: true },
        categories: {
            'Gruppenstunde': { push: true, email: true },
            'Lager': { push: true, email: true },
            'Aktion': { push: true, email: true },
            'Sonstiges': { push: true, email: true }
        }
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [vapidPublicKey, setVapidPublicKey] = useState(null);
    const [showEventDetails, setShowEventDetails] = useState(true);
    const [showTutorial, setShowTutorial] = useState(!hasSeenTutorial('notifications'));

    const authConfig = {
        headers: {
            Authorization: `Bearer ${user?.token}`
        }
    };

    useEffect(() => {
        if (user) {
            fetchSettings();
            fetchPublicKey();
            checkSubscription();
        }
    }, [user]);

    const fetchPublicKey = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/notifications/public-key`);
            setVapidPublicKey(data.publicKey);
        } catch (error) {
            console.error('Error fetching VAPID public key:', error);
        }
    };

    const fetchSettings = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/notifications/settings`, authConfig);
            if (data && data.events) {
                const categories = data.categories || {
                    'Gruppenstunde': { push: true, email: true },
                    'Lager': { push: true, email: true },
                    'Aktion': { push: true, email: true },
                    'Sonstiges': { push: true, email: true }
                };
                setSettings({ ...data, categories });
            }
        } catch (error) {
            console.error('Error fetching notification settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkSubscription = async () => {
        if ('serviceWorker' in navigator) {
            try {
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.getSubscription();
                setIsSubscribed(!!subscription);
            } catch (err) {
                console.error("SW ready check failed:", err);
            }
        }
    };

    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const handleSubscribe = async () => {
        try {
            setSaving(true);
            const registration = await navigator.serviceWorker.ready;
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') throw new Error('Berechtigung abgelehnt.');
            if (!vapidPublicKey) throw new Error('Public Key fehlt.');

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
            });

            await axios.post(`${API_URL}/notifications/subscribe`, { subscription }, authConfig);
            setIsSubscribed(true);
            setMessage('Push-Benachrichtigungen aktiv!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Fehler: ' + (error.response?.data?.message || error.message));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSettings = async () => {
        try {
            setSaving(true);
            const { data } = await axios.put(`${API_URL}/notifications/settings`, { settings }, authConfig);
            setSettings(data);
            setMessage('Einstellugen gespeichert!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Fehler beim Speichern.');
        } finally {
            setSaving(false);
        }
    };

    const toggleSetting = (topic, channel) => {
        setSettings(prev => ({
            ...prev,
            [topic]: { ...prev[topic], [channel]: !prev[topic][channel] }
        }));
    };

    const toggleCategorySetting = (category, channel) => {
        setSettings(prev => ({
            ...prev,
            categories: {
                ...prev.categories,
                [category]: { ...prev.categories[category], [channel]: !prev.categories[category][channel] }
            }
        }));
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-20 animate-fade-in text-surface-onVariant gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary" />
            <p className="font-bold tracking-wide">Lade Einstellungen...</p>
        </div>
    );

    const eventCategories = ['Gruppenstunde', 'Lager', 'Aktion', 'Sonstiges'];

    return (
        <div className="max-w-4xl mx-auto px-6 py-8 md:py-12 animate-fade-in pb-20">
            {showTutorial && (
                <TutorialModal pageId="notifications" onClose={() => setShowTutorial(false)} />
            )}
            <header className="mb-10">
                <h1 className="text-display-small font-bold text-surface-on mb-3">Benachrichtigungen</h1>
                <p className="text-body-large text-surface-onVariant max-w-xl opacity-80 leading-relaxed">
                    Personalisiere dein Erlebnis. Wähle aus, welche Informationen du erhalten möchtest.
                </p>
            </header>

            {/* Link to Inbox */}
            <a
                href="/inbox"
                className="md3-card-elevated !bg-primary-container !text-primary-onContainer p-6 mb-10 flex items-center gap-6 group transition-all hover:shadow-elevation-2 animate-scale-in border border-primary/10 cursor-pointer no-underline"
                style={{ textDecoration: 'none' }}
            >
                <div className="w-14 h-14 rounded-[1.5rem] bg-primary text-primary-on flex items-center justify-center shrink-0 shadow-elevation-1 group-hover:scale-110 transition-transform">
                    <Mail size={28} />
                </div>
                <div className="flex-1">
                    <h2 className="text-title-large font-bold mb-1">Postfach öffnen</h2>
                    <p className="text-body-medium font-medium opacity-80">
                        Alle deine Benachrichtigungen an einem Ort – mit Aktions-Buttons für schnelle Entscheidungen.
                    </p>
                </div>
                <ChevronRight className="text-primary opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </a>

            {!isSubscribed && (
                <div className="md3-card-elevated !bg-tertiary-container !text-tertiary-onContainer p-8 mb-12 flex flex-col md:flex-row items-center gap-8 animate-scale-in border border-tertiary/20">
                    <div className="w-16 h-16 rounded-[2rem] bg-tertiary text-tertiary-on flex items-center justify-center shrink-0 shadow-elevation-2">
                        <Smartphone size={32} />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                        <h2 className="text-headline-small font-bold mb-2">Push-Benachrichtigungen aktivieren</h2>
                        <p className="text-body-medium font-medium opacity-90 leading-relaxed">
                            Erhalte wichtige Updates direkt auf dein Gerät, ohne die E-Mails prüfen zu müssen.
                        </p>
                    </div>
                    <button
                        onClick={handleSubscribe}
                        disabled={saving}
                        className="md3-btn-filled !bg-tertiary !text-tertiary-on hover:scale-105 active:scale-95 transition-all h-14 px-8 shadow-elevation-2"
                    >
                        {saving ? <Loader2 className="animate-spin" /> : 'Jetzt aktivieren'}
                    </button>
                </div>
            )}

            <div className="md3-card-elevated !bg-surface-containerLow overflow-hidden border border-outlineVariant/30 shadow-elevation-2 mb-10">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-surface-containerHigh/50">
                                <th className="p-6 text-label-large font-bold text-surface-on uppercase tracking-widest text-xs opacity-60">Thema</th>
                                <th className="p-6 text-center w-36 border-l border-outlineVariant/10">
                                    <div className="flex flex-col items-center gap-2">
                                        <Smartphone size={18} className="text-primary" />
                                        <span className="text-label-small font-bold text-surface-on">PUSH</span>
                                    </div>
                                </th>
                                <th className="p-6 text-center w-36 border-l border-outlineVariant/10">
                                    <div className="flex flex-col items-center gap-2">
                                        <Mail size={18} className="text-secondary" />
                                        <span className="text-label-small font-bold text-surface-on">E-MAIL</span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-outlineVariant/10">
                            {/* Events Row */}
                            <tr className="group transition-colors">
                                <td className="p-6 cursor-pointer" onClick={() => setShowEventDetails(!showEventDetails)}>
                                    <div className="flex items-center gap-6">
                                        <div className="p-4 rounded-2xl bg-secondary-container text-secondary-onContainer transition-transform group-hover:scale-110 shadow-sm">
                                            <Bell size={24} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-title-large font-bold text-surface-on">Veranstaltungen</h3>
                                                {showEventDetails ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                            </div>
                                            <p className="text-body-small text-surface-onVariant font-medium">Neue Aktionen und Anmeldungen</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-6 text-center border-l border-outlineVariant/10">
                                    <MD3Switch
                                        checked={settings.events.push}
                                        onChange={() => toggleSetting('events', 'push')}
                                        color="primary"
                                    />
                                </td>
                                <td className="p-6 text-center border-l border-outlineVariant/10">
                                    <MD3Switch
                                        checked={settings.events.email}
                                        onChange={() => toggleSetting('events', 'email')}
                                        color="secondary"
                                    />
                                </td>
                            </tr>

                            {/* Detailed Categories */}
                            {showEventDetails && eventCategories.map(cat => (
                                <tr key={cat} className="bg-surface/30">
                                    <td className="py-4 pl-20 pr-6">
                                        <div className="flex items-center gap-2 border-l-4 border-secondary/20 pl-4 py-1">
                                            <span className="text-title-small font-bold text-surface-onVariant">{cat}</span>
                                        </div>
                                    </td>
                                    <td className="py-4 px-6 text-center border-l border-outlineVariant/10">
                                        <div className="scale-75 origin-center">
                                            <MD3Switch
                                                checked={settings.categories?.[cat]?.push ?? true}
                                                onChange={() => toggleCategorySetting(cat, 'push')}
                                            />
                                        </div>
                                    </td>
                                    <td className="py-4 px-6 text-center border-l border-outlineVariant/10">
                                        <div className="scale-75 origin-center">
                                            <MD3Switch
                                                checked={settings.categories?.[cat]?.email ?? true}
                                                onChange={() => toggleCategorySetting(cat, 'email')}
                                                color="secondary"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {/* Blog Row */}
                            <tr className="group transition-colors">
                                <td className="p-6">
                                    <div className="flex items-center gap-6">
                                        <div className="p-4 rounded-2xl bg-primary-container text-primary-onContainer transition-transform group-hover:scale-110 shadow-sm">
                                            <FileText size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-title-large font-bold text-surface-on">Blogbeiträge</h3>
                                            <p className="text-body-small text-surface-onVariant font-medium">Neue Berichte und Neuigkeiten</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-6 text-center border-l border-outlineVariant/10">
                                    <MD3Switch
                                        checked={settings.blog.push}
                                        onChange={() => toggleSetting('blog', 'push')}
                                    />
                                </td>
                                <td className="p-6 text-center border-l border-outlineVariant/10">
                                    <MD3Switch
                                        checked={settings.blog.email}
                                        onChange={() => toggleSetting('blog', 'email')}
                                        color="secondary"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="p-8 bg-surface-containerHigh/50 border-t border-outlineVariant/20 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="min-h-8">
                        {message && (
                            <div className={`flex items-center gap-3 font-bold px-4 py-2 rounded-xl animate-fade-in ${message.includes('Fehler') ? 'bg-error/10 text-error' : 'bg-primary/10 text-primary'
                                }`}>
                                {message.includes('Fehler') ? <ShieldAlert size={20} /> : <CheckCircle2 size={20} />}
                                <span className="text-sm font-bold uppercase tracking-widest">{message}</span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={handleSaveSettings}
                        disabled={saving}
                        className="md3-btn-filled h-14 px-10 gap-4 !rounded-2xl transition-all shadow-elevation-2 active:scale-95"
                    >
                        {saving ? <Loader2 size={24} className="animate-spin" /> : <Save size={24} />}
                        <span className="text-lg font-bold">Änderungen speichern</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Notifications;
